import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";
import { Role } from "@/types/organization-roles";

interface CreateOrganizationRoleRequest {
  organizationId: number;
  name: string;
  permissions: string[];
}

interface CreateOrganizationRoleResponse {
  role: Role;
}

export const useCreateOrganizationRole = () => {
  const queryClient = useQueryClient();
  const userToken = useUserToken();

  return useMutation(
    ({ organizationId, name, permissions }: CreateOrganizationRoleRequest) =>
      fetch(ENDPOINTS.create_organization_role(organizationId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({ name, permissions }),
      }).then(handleResponse) as Promise<CreateOrganizationRoleResponse>,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["organization-roles"]);
        displayToast("Role created successfully", ToastType.success);
      },
      onError: (error: unknown) => {
        displayErrorToast(
          typeof error === "string" ? error : "Failed to create role. Please try again.",
        );
      },
    },
  );
};
